import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle, XCircle, TrendingUp, TrendingDown, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import EmptyState from '@/components/EmptyState';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', slate: '#5C6B7A' };
const fmt$ = (v) => `$${Math.round(v || 0).toLocaleString()}`;
const STATUS_COLORS = { quote_generated: C.blue, offered: C.teal, accepted: C.green, declined: C.red, expired: C.slate, changes_requested: '#B27516' };

export default function BrokerRenewalsPage() {
  const api = useApi();
  const [renewals, setRenewals] = useState(null);
  const [busy, setBusy] = useState('');
  const [changesFor, setChangesFor] = useState('');
  const [changeNotes, setChangeNotes] = useState('');

  const load = useCallback(() => {
    api.get('/renewals?limit=100').then(r => setRenewals(r.data)).catch(err => { console.error('API error:', err?.message); setRenewals([]); });
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const act = async (renewalId, action, notes = '') => {
    setBusy(renewalId);
    try {
      await api.put(`/renewals/${renewalId}/action`, { action, notes });
      toast.success(action === 'request_changes' ? 'Change request sent to the underwriter' : `Renewal ${action}ed`);
      setChangesFor(''); setChangeNotes('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
    setBusy('');
  };

  if (renewals === null) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;

  if (renewals.length === 0) {
    return <EmptyState type="renewals" />;
  }

  return (
    <div className="space-y-3" data-testid="broker-renewals-page">
      {renewals.map(r => {
        const up = (r.premium_change_pct || 0) >= 0;
        const actionable = ['quote_generated', 'offered'].includes(r.status);
        return (
          <Card key={r.renewal_id} className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid={`broker-renewal-${r.renewal_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold" style={{ color: C.navy }}>{r.business_name}</p>
                <p className="text-xs mt-0.5" style={{ color: C.slate }}>
                  {r.original_policy_number} · {r.insurance_class} · expires in {r.days_to_expiry} days
                </p>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>Current → Renewal</p>
                  <p className="text-sm font-mono font-bold" style={{ color: C.navy }}>
                    {fmt$(r.current_premium)} → {fmt$(r.renewal_premium)}
                    <span className="ml-2 text-xs inline-flex items-center gap-0.5" style={{ color: up ? C.red : C.green }}>
                      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{r.premium_change_pct}%
                    </span>
                  </p>
                </div>
                <span className="text-[10px] px-2 py-1 rounded-full font-semibold uppercase tracking-wider"
                  style={{ background: (STATUS_COLORS[r.status] || C.slate) + '18', color: STATUS_COLORS[r.status] || C.slate }}>
                  {(r.status || '').replace('_', ' ')}
                </span>
                {actionable && (
                  <div className="flex gap-2">
                    <button onClick={() => act(r.renewal_id, 'accept')} disabled={busy === r.renewal_id}
                      data-testid={`accept-renewal-${r.renewal_id}`}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs font-semibold text-white" style={{ background: C.green }}>
                      <CheckCircle className="w-3.5 h-3.5" /> Accept
                    </button>
                    <button onClick={() => setChangesFor(changesFor === r.renewal_id ? '' : r.renewal_id)} disabled={busy === r.renewal_id}
                      data-testid={`request-changes-renewal-${r.renewal_id}`}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs font-semibold" style={{ color: '#B27516', border: '1px solid rgba(232,160,32,0.5)' }}>
                      <MessageSquare className="w-3.5 h-3.5" /> Request Changes
                    </button>
                    <button onClick={() => act(r.renewal_id, 'decline')} disabled={busy === r.renewal_id}
                      data-testid={`decline-renewal-${r.renewal_id}`}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs font-semibold" style={{ color: C.red, border: `1px solid ${C.red}40` }}>
                      <XCircle className="w-3.5 h-3.5" /> Decline
                    </button>
                  </div>
                )}
              </div>
            </div>
            {r.status === 'changes_requested' && r.response_notes && (
              <p className="mt-2 text-xs px-2 py-1.5 rounded-sm" style={{ background: 'rgba(232,160,32,0.08)', color: '#B27516' }}>
                Changes requested: <span style={{ color: C.navy }}>{r.response_notes}</span>
              </p>
            )}
            {changesFor === r.renewal_id && (
              <div className="mt-3 space-y-2" data-testid={`changes-panel-${r.renewal_id}`}>
                <textarea value={changeNotes} onChange={e => setChangeNotes(e.target.value)} rows={2}
                  placeholder="What would you like changed? (e.g. lower excess, adjust sum insured, remove claims loading)"
                  className="w-full px-3 py-2 text-xs rounded-md border outline-none focus:border-[#2A6FC4]"
                  style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} data-testid={`changes-notes-${r.renewal_id}`} />
                <button onClick={() => act(r.renewal_id, 'request_changes', changeNotes)} disabled={busy === r.renewal_id || !changeNotes.trim()}
                  data-testid={`changes-submit-${r.renewal_id}`}
                  className="px-3 py-1.5 rounded-sm text-xs font-semibold text-white disabled:opacity-40" style={{ background: '#E8A020' }}>
                  Send Change Request
                </button>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
